import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme, TOKENS } from '../context/ThemeContext';
import Navbar from '../components/Navbar';
import SEO from '../components/SEO';

const faqs = [
  {
    category: 'Getting Started',
    items: [
      { q: 'What is TrailCode?', a: 'TrailCode is a hands-on learning platform for Salesforce developers. You write real Apex code, validate it against your actual Salesforce org and get instant feedback on every test. No simulators, no multiple choice — just real code running in a real org.' },
      { q: 'Do I need a Salesforce org to use TrailCode?', a: "Yes — and that's the point. Your code runs in your actual Salesforce dev org, not a simulator. This means the feedback you get is real. If you don't have one, sign up for a free Developer Edition at developer.salesforce.com — it takes 2 minutes." },
      { q: 'Which orgs are supported?', a: 'Production orgs, Developer Edition orgs and Sandboxes are all supported. When connecting your org, choose "Production / Dev Org" for production and developer edition orgs, or "Sandbox" for sandbox environments.' },
      { q: 'Is TrailCode free?', a: 'All Easy challenges are completely free for everyone — no account required. Free accounts also get one Medium challenge per topic to try before upgrading. Pro unlocks everything for $9/month.' },
    ]
  },
  {
    category: 'Challenges',
    items: [
      { q: 'Why do challenges unlock one at a time?', a: 'Each challenge builds on the previous one. Completing challenge 1 unlocks challenge 2, and so on. This ensures you learn concepts in the right order rather than jumping to advanced topics before the foundations are solid.' },
      { q: "My code looks correct but tests are failing — why?", a: "A few common reasons: your class name or method name doesn't match exactly (Apex is case sensitive), you're missing a return statement, or a field name is wrong. Read the Requirements section carefully — every constraint is tested explicitly." },
      { q: 'Can I reset a challenge and start over?', a: 'Yes — once you\'ve solved a challenge, a "Reset & Practice Again" button appears. This clears your solution back to the starter code so you can practice the concept again.' },
      { q: 'What happens to the classes deployed to my org?', a: 'TrailCode deploys your class and the test class temporarily to run the tests, then immediately deletes both. Your org is left exactly as it was. No permanent changes are made.' },
      { q: 'Why does validation take a few seconds?', a: "Salesforce needs to compile and deploy your Apex class, run all the test methods and return the results. This typically takes 5-15 seconds depending on your org's load. It's real test execution — not a simulation." },
    ]
  },
  {
    category: 'Account & Org',
    items: [
      { q: 'My Salesforce session keeps expiring — what do I do?', a: "Salesforce OAuth tokens expire after a few hours. When this happens you'll see a \"Session expired\" message with a Reconnect button. Click it, re-authorise your org and you're back. This is normal behaviour for any Salesforce connected app." },
      { q: 'Is my Salesforce code or org data visible to TrailCode?', a: "No. TrailCode only deploys the code you write in the editor and the hidden test class. It reads the test results and immediately deletes both classes. We never access your org data, existing code or any records." },
      { q: 'Can I use TrailCode with multiple orgs?', a: 'Yes — you can disconnect your current org and connect a different one at any time from the challenge page. Your progress is saved to your account regardless of which org you use.' },
    ]
  },
  {
    category: 'Technical',
    items: [
      { q: 'What Salesforce API version does TrailCode use?', a: 'TrailCode uses the Salesforce Tooling API with the latest stable API version. It works with all Developer Edition, Sandbox and Production orgs on supported API versions.' },
      { q: 'Does TrailCode work with Scratch Orgs?', a: "Not currently — Scratch Orgs use a different authentication flow. Support for Scratch Orgs is on the roadmap. For now, use a Developer Edition org which is free and always available." },
      { q: 'Can companies use TrailCode for team training?', a: 'Yes — the Team plan gives you 5 seats and a shared dashboard. Contact us at hello@trailcode.org to discuss company plans and team onboarding.' },
    ]
  },
];

function FAQ() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const c = TOKENS[theme];
  const [openItems, setOpenItems] = useState({});

  const toggle = (key) => setOpenItems(prev => ({ ...prev, [key]: !prev[key] }));

  return (
    <div style={{ background: c.bg, minHeight: '100vh', color: c.text, fontFamily: "'Inter', -apple-system, sans-serif", transition: 'background 0.25s, color 0.25s' }}>
      <SEO title="FAQ — TrailCode" description="Frequently asked questions about TrailCode." />
      <Navbar />

      <div style={{ textAlign: 'center', padding: '60px 20px 40px' }}>
        <h1 style={{ fontSize: '36px', fontWeight: '700', marginBottom: '12px', letterSpacing: '-0.5px', color: c.text }}>Frequently Asked Questions</h1>
        <p style={{ color: c.secondary, fontSize: '16px', maxWidth: '480px', margin: '0 auto' }}>
          Everything you need to know about TrailCode. Can't find your answer?{' '}
          <span style={{ color: c.accent, cursor: 'pointer', fontWeight: 500 }} onClick={() => navigate('/contact')}>Contact us →</span>
        </p>
      </div>

      <div style={{ maxWidth: '720px', margin: '0 auto', padding: '0 40px 80px' }}>
        {faqs.map((section) => (
          <div key={section.category} style={{ marginBottom: '40px' }}>
            <h2 style={{ fontSize: '13px', fontWeight: '700', color: c.accent, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '16px' }}>{section.category}</h2>
            {section.items.map((item, i) => {
              const key = `${section.category}-${i}`;
              const isOpen = openItems[key];
              return (
                <div key={i} style={{ border: `1px solid ${c.border}`, borderRadius: '10px', marginBottom: '8px', overflow: 'hidden', background: c.card, boxShadow: isOpen ? `0 2px 12px rgba(0,0,0,0.1)` : 'none' }}>
                  <button onClick={() => toggle(key)} style={{ width: '100%', textAlign: 'left', background: isOpen ? c.cardHover : c.card, border: 'none', padding: '18px 20px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', transition: 'background 0.15s' }}>
                    <span style={{ fontSize: '15px', fontWeight: '500', color: c.text, paddingRight: '16px' }}>{item.q}</span>
                    <span style={{ color: c.accent, fontSize: '18px', flexShrink: 0, transition: 'transform 0.2s', transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)' }}>+</span>
                  </button>
                  {isOpen && (
                    <div style={{ padding: '0 20px 18px', borderTop: `1px solid ${c.border}`, background: c.cardHover }}>
                      <p style={{ fontSize: '14px', color: c.secondary, lineHeight: '1.8', marginTop: '14px' }}>{item.a}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}

        <div style={{ background: c.accentBg, border: `1px solid ${c.accentBorder}`, borderRadius: '12px', padding: '28px', textAlign: 'center' }}>
          <p style={{ fontSize: '16px', fontWeight: '600', color: c.text, marginBottom: '8px' }}>Still have questions?</p>
          <p style={{ fontSize: '14px', color: c.secondary, marginBottom: '18px' }}>We're happy to help. Email us at <a href="mailto:hello@trailcode.org" style={{ color: c.accent }}>hello@trailcode.org</a></p>
          <button onClick={() => navigate('/contact')} style={{ background: c.accent, color: theme === 'dark' ? c.bg : '#fff', border: 'none', padding: '10px 24px', borderRadius: '7px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
            Contact Support →
          </button>
        </div>
      </div>
    </div>
  );
}

export default FAQ;